import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { VUKZ } from '../theme/vukzTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import { listSessions, cancelSession, reprocessSession } from '../services/biomechanicsService';
import {
  Activity, BarChart3, CheckCircle2, Clock, AlertTriangle, XCircle,
  Video, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight,
  RefreshCw, Loader2, Ban, Tag, Link2, Filter, Search, Eye,
} from 'lucide-react';

const T = {
  bg: '#fafafa',
  card: '#ffffff',
  text: '#111111',
  textSoft: '#6b7280',
  textMute: '#9ca3af',
  border: '#e5e7eb',
  accent: '#111111',
  accentSoft: '#f3f4f6',
  success: '#16a34a',
  successBg: '#f0fdf4',
  warning: '#f59e0b',
  warningBg: '#fffbeb',
  error: '#dc2626',
  errorBg: '#fef2f2',
  info: '#2563eb',
  infoBg: '#eff6ff',
  cyan: '#0891b2',
};

const LABELS_I18N = {
  pt: {
    styleLabels: { freestyle: 'Livre', backstroke: 'Costas', butterfly: 'Borboleta', breaststroke: 'Peito', medley: 'Medley' },
    statusMeta: { completed: 'Concluída', processing: 'Processando', pending: 'Na fila', failed: 'Falha', cancelled: 'Cancelada' },
    reprocessTitle: 'Reprocessar', cancelTitle: 'Cancelar',
    viewResultTitle: 'Ver resultado', annotateTitle: 'Anotar vídeo',
    allSessions: 'Todas as Sessões',
    totalSessions: (n) => `${n} sessões no total`, loading: 'Carregando...',
    newAnalysis: 'Nova Análise',
    allStatuses: 'Todos os status', allStyles: 'Todos os estilos', allDistances: 'Todas as provas',
    statusOptions: { completed: 'Concluídas', processing: 'Processando', pending: 'Na fila', failed: 'Falhas', cancelled: 'Canceladas' },
    clearFilters: 'Limpar filtros',
    loadingSessions: 'Carregando sessões...',
    noSessions: 'Nenhuma sessão encontrada', tryFilters: 'Tente ajustar os filtros',
    confirmCancel: (id, st) => `Cancelar sessão #${id} (${st})?`,
    confirmReprocess: (id) => `Reprocessar sessão #${id}?`,
    errCancel: 'Erro ao cancelar', errReprocess: 'Erro ao reprocessar',
    linkedTitle: 'Vinculada a resultado oficial', strokesAbbr: 'braç.',
  },
  en: {
    styleLabels: { freestyle: 'Freestyle', backstroke: 'Backstroke', butterfly: 'Butterfly', breaststroke: 'Breaststroke', medley: 'Medley' },
    statusMeta: { completed: 'Completed', processing: 'Processing', pending: 'In queue', failed: 'Failed', cancelled: 'Cancelled' },
    reprocessTitle: 'Reprocess', cancelTitle: 'Cancel',
    viewResultTitle: 'View result', annotateTitle: 'Annotate video',
    allSessions: 'All Sessions',
    totalSessions: (n) => `${n} sessions total`, loading: 'Loading...',
    newAnalysis: 'New Analysis',
    allStatuses: 'All statuses', allStyles: 'All styles', allDistances: 'All events',
    statusOptions: { completed: 'Completed', processing: 'Processing', pending: 'In queue', failed: 'Failed', cancelled: 'Cancelled' },
    clearFilters: 'Clear filters',
    loadingSessions: 'Loading sessions...',
    noSessions: 'No sessions found', tryFilters: 'Try adjusting the filters',
    confirmCancel: (id, st) => `Cancel session #${id} (${st})?`,
    confirmReprocess: (id) => `Reprocess session #${id}?`,
    errCancel: 'Error cancelling', errReprocess: 'Error reprocessing',
    linkedTitle: 'Linked to official result', strokesAbbr: 'str.',
  },
};

const STYLE_COLORS = {
  freestyle: '#2563eb', backstroke: '#7c3aed', butterfly: '#ea580c',
  breaststroke: '#16a34a', medley: '#0891b2',
};

const PAGE_SIZE = 30;

const SessionRow = ({ s, index, offset, onCancel, onReprocess }) => {
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;
  const STATUS_META = {
    completed: { icon: CheckCircle2, color: T.success, bg: T.successBg },
    processing: { icon: Loader2, color: T.info, bg: T.infoBg },
    pending:    { icon: Clock, color: T.warning, bg: T.warningBg },
    failed:     { icon: XCircle, color: T.error, bg: T.errorBg },
    cancelled:  { icon: Ban, color: T.textMute, bg: T.accentSoft },
  };
  const STYLE_LABELS = L.styleLabels;
  const meta = STATUS_META[s.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const canCancel = s.status === 'pending' || s.status === 'processing';
  const canReprocess = s.status === 'failed' || s.status === 'cancelled';

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b"
      style={{ borderColor: T.border }}>
      <span className="text-[10px] font-bold w-6 text-center" style={{ color: T.textMute }}>
        {offset + index + 1}
      </span>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: meta.bg }}>
        <StatusIcon size={14} style={{ color: meta.color }} className={s.status === 'processing' ? 'animate-spin' : ''} />
      </div>
      <Link
        to={s.status === 'completed' ? `/swim-vision/result/${s.session_id}` : `/swim-vision/session/${s.session_id}/status`}
        className="flex-1 min-w-0"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold truncate" style={{ color: T.text }}>
            #{s.session_id}
          </span>
          {s.swimming_style && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: (STYLE_COLORS[s.swimming_style] || '#666') + '18', color: STYLE_COLORS[s.swimming_style] || '#666' }}>
              {STYLE_LABELS[s.swimming_style] || s.swimming_style}
            </span>
          )}
          {s.official_result_id && (
            <Link2 size={10} style={{ color: T.cyan }} title={L.linkedTitle} />
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] truncate" style={{ color: T.textMute }}>
            {s.video_filename ? s.video_filename.substring(0, 50) : '—'}
          </span>
          {s.created_at && (
            <span className="text-[10px] shrink-0" style={{ color: T.textMute }}>
              {new Date(s.created_at).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
      </Link>
      <div className="text-right shrink-0 min-w-[60px]">
        {s.stroke_count != null && (
          <div className="text-xs font-bold" style={{ color: T.text }}>
            {s.stroke_count} <span style={{ color: T.textMute }}>{L.strokesAbbr}</span>
          </div>
        )}
        {s.processing_time_ms != null && (
          <div className="text-[10px]" style={{ color: T.textMute }}>
            {(s.processing_time_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canReprocess && (
          <button onClick={() => onReprocess(s.session_id, s.status)}
            className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title={L.reprocessTitle}>
            <RefreshCw size={14} style={{ color: T.accent }} />
          </button>
        )}
        {canCancel && (
          <button onClick={() => onCancel(s.session_id, s.status)}
            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title={L.cancelTitle}>
            <Ban size={14} style={{ color: T.error }} />
          </button>
        )}
        {s.status === 'completed' && (
          <Link to={`/swim-vision/result/${s.session_id}`}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title={L.viewResultTitle}>
            <BarChart3 size={14} style={{ color: T.accent }} />
          </Link>
        )}
        {s.status === 'completed' && (
          <Link to={`/swim-vision/annotate/${s.session_id}`}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title={L.annotateTitle}>
            <Tag size={14} style={{ color: T.cyan }} />
          </Link>
        )}
        <ChevronRight size={14} style={{ color: T.textMute }} />
      </div>
    </div>
  );
};

export default function SwimVisionSessionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const filterStatus = searchParams.get('status') || '';
  const filterStyle = searchParams.get('style') || '';
  const filterDistance = searchParams.get('distance') || '';

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const offset = (currentPage - 1) * PAGE_SIZE;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (filterStatus) params.status = filterStatus;
      if (filterStyle) params.style = filterStyle;
      if (filterDistance) params.distance = filterDistance;
      const resp = await listSessions(params);
      const body = resp.data;
      const d = body?.data || body;
      setSessions(d?.sessions || d?.items || (Array.isArray(d) ? d : []));
      setTotal(body?.meta?.total || d?.total || d?.count || (Array.isArray(d) ? d.length : 0));
    } catch (err) {
      console.error('[Sessions] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [offset, filterStatus, filterStyle, filterDistance]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(p));
    setSearchParams(params);
  };

  const setFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCancel = async (sessionId, status) => {
    if (!window.confirm(L.confirmCancel(sessionId, status))) return;
    try {
      await cancelSession(sessionId, `Cancelled from sessions list`);
      await fetchSessions();
    } catch (err) {
      alert(`${L.errCancel}: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleReprocess = async (sessionId, status) => {
    if (!window.confirm(L.confirmReprocess(sessionId))) return;
    try {
      await reprocessSession(sessionId);
      await fetchSessions();
    } catch (err) {
      alert(`${L.errReprocess}: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/swim-vision/dashboard')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} style={{ color: T.text }} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-black" style={{ color: T.text }}>{L.allSessions}</h1>
            <p className="text-xs" style={{ color: T.textSoft }}>
              {total > 0 ? L.totalSessions(total) : L.loading}
            </p>
          </div>
          <Link to="/swim-vision"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: T.accent }}>
            <Video size={14} /> {L.newAnalysis}
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-3 mb-4 flex flex-wrap items-center gap-3" style={{ borderColor: T.border }}>
          <Filter size={14} style={{ color: T.textSoft }} />
          <select value={filterStatus} onChange={(e) => setFilter('status', e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: T.border, color: T.text }}>
            <option value="">{L.allStatuses}</option>
            <option value="completed">{L.statusOptions.completed}</option>
            <option value="processing">{L.statusOptions.processing}</option>
            <option value="pending">{L.statusOptions.pending}</option>
            <option value="failed">{L.statusOptions.failed}</option>
            <option value="cancelled">{L.statusOptions.cancelled}</option>
          </select>
          <select value={filterStyle} onChange={(e) => setFilter('style', e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: T.border, color: T.text }}>
            <option value="">{L.allStyles}</option>
            <option value="freestyle">{L.styleLabels.freestyle}</option>
            <option value="backstroke">{L.styleLabels.backstroke}</option>
            <option value="butterfly">{L.styleLabels.butterfly}</option>
            <option value="breaststroke">{L.styleLabels.breaststroke}</option>
            <option value="medley">{L.styleLabels.medley}</option>
          </select>
          <select value={filterDistance} onChange={(e) => setFilter('distance', e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: T.border, color: T.text }}>
            <option value="">{L.allDistances}</option>
            <option value="50">50m</option>
            <option value="100">100m</option>
            <option value="200">200m</option>
            <option value="400">400m</option>
            <option value="800">800m</option>
            <option value="1500">1500m</option>
          </select>
          {(filterStatus || filterStyle || filterDistance) && (
            <button onClick={() => { const p = new URLSearchParams(); p.set('page', '1'); setSearchParams(p); }}
              className="text-[10px] font-bold px-2 py-1 rounded hover:bg-gray-100" style={{ color: T.error }}>
              {L.clearFilters}
            </button>
          )}
        </div>

        {/* Sessions list */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: T.border }}>
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" style={{ color: T.accent }} />
              <p className="text-xs" style={{ color: T.textSoft }}>{L.loadingSessions}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-12 text-center">
              <Video size={24} style={{ color: T.textMute }} className="mx-auto mb-2" />
              <p className="text-xs font-semibold" style={{ color: T.textSoft }}>{L.noSessions}</p>
              {(filterStatus || filterStyle || filterDistance) && (
                <p className="text-[10px] mt-1" style={{ color: T.textMute }}>{L.tryFilters}</p>
              )}
            </div>
          ) : (
            <div>
              {sessions.map((s, i) => (
                <SessionRow
                  key={s.session_id}
                  s={s}
                  index={i}
                  offset={offset}
                  onCancel={handleCancel}
                  onReprocess={handleReprocess}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ borderColor: T.border }}>
              <ChevronLeft size={16} style={{ color: T.text }} />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }
                return (
                  <button key={page} onClick={() => goToPage(page)}
                    className="w-8 h-8 rounded-lg text-xs font-bold transition-colors"
                    style={{
                      backgroundColor: page === currentPage ? T.accent : 'transparent',
                      color: page === currentPage ? '#fff' : T.textSoft,
                    }}>
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ borderColor: T.border }}>
              <ChevronRight size={16} style={{ color: T.text }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
